import React, { useContext } from 'react';
import { TableContext } from '../../../../contexts/TableContext';

import Dropdown from '../../../../components/Dropdown';

interface DocumentRowProps {
  item: any;
  index: number;
  selectedItems: any[];
  onToggleSelect: (item: any) => void;
  onChange: (value: any, name: string) => void;
  t: any;
  view: boolean;
  formData: any;
  documentTypes: any;
  errors?: any;
  onDelete?: (item: any) => void;
}

const DocumentRow: React.FC<DocumentRowProps> = ({ item, index, selectedItems, onToggleSelect, onChange, t, view, formData, documentTypes, errors, onDelete }) => {
  const { headCells } = useContext(TableContext);

  const isNew = item.file instanceof File || item instanceof File;
  const fileName = isNew ? (item.name || item.file?.name) : item.doc_name || item.name || 'Document';
  const fileUrl = isNew ? '#' : item.doc_url;
  const isSelected = selectedItems.includes(item);

  // Use the index prop directly from TableBody
  const errorKey = `documents-${index}-doc_type`;
  const errorMessage = errors ? errors[errorKey] : null;

  const handleTypeChange = (e: any, name: string) => {
    const val = e.target.value;
    // Use the same matching logic to update the correct document
    const newDocs = formData.documents.map((doc: any, idx: number) => {
      // Use index-based matching as most reliable
      if (idx === index) {
        return { ...doc, doc_type: val };
      }
      return doc;
    });
    onChange(newDocs, 'documents');
  };

  const handleDownload = () => {
    let url = item.doc_url;

    if (isNew) {
      const file = item.file instanceof File ? item.file : item;
      if (file) {
        url = URL.createObjectURL(file);
      }
    }

    if (!url || url === '#') return;

    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank"; // open in new tab
    a.rel = "noopener noreferrer"; // security best practice
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const getDocOptions = () => {
    if (Array.isArray(documentTypes) && documentTypes.length > 0) {
      return documentTypes.map((type: any) => ({
        label: type.name,
        value: type.id
      }));
    }
    return [
      { label: 'Floor Plan', value: 'Floor Plan' },
      { label: 'Site Map', value: 'Site Map' },
      { label: 'Legal', value: 'Legal' },
      { label: 'Payment Plan', value: 'Payment Plan' },
      { label: 'Brochure', value: 'Brochure' },
      { label: 'Other', value: 'Other' }
    ];
  };

  const docOptions = getDocOptions();

  return (
    <tr>
      {headCells.map((headCell) => {
        if (headCell.id === 'select') {
          return (
            !view ? (
              <td key={headCell.id} className="text-center" width={headCell.width}>
                <div className="form-check d-flex justify-content-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(item)}
                  />
                </div>
              </td>
            ) : null
          );
        }

        if (headCell.id === 'name') {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-center">
                <i className="ri-file-text-line fs-4 me-2 text-primary"></i>
                {isNew ? (
                  <span>{fileName}</span>
                ) : (
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-underline"
                  >
                    {fileName}
                  </a>
                )}
              </div>
            </td>
          );
        }

        if (headCell.id === 'doc_type') {
          const getDocTypeName = (val: any) => {
            if (!val) return 'Document';
            if (Array.isArray(documentTypes) && documentTypes.length > 0) {
              const found = documentTypes.find((dt: any) => dt.id == val);
              return found ? found.name : val;
            }
            return val;
          };

          const docTypeLabel = getDocTypeName(item.doc_type);

          return (
            <td key={headCell.id} width={headCell.width}>
              {!view && isNew ? (
                <div style={{ minWidth: '150px' }}>
                  <Dropdown
                    id={`doc-type-${index}`}
                    name={`doc_type_${index}`}
                    label=""
                    values={item.doc_type ? [item.doc_type] : []}
                    options={docOptions}
                    allOptions={docOptions}
                    onChange={handleTypeChange}
                    error={!!errorMessage}
                    errorMessage={errorMessage}
                    noSearch
                    placeholder={t('common.dropdown.select') || 'Select Type'}
                  />
                </div>
              ) : (
                <span className="badge bg-label-secondary">
                  {docTypeLabel || 'Document'}
                </span>
              )}
            </td>
          );
        }

        if (headCell.id === 'actions') {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-text-secondary rounded-pill"
                  title={t("common.buttons.view") || "View"}
                  onClick={handleDownload}
                >
                  <i className="ri-eye-line fs-5" />
                </button>

                {!view && (
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-text-danger rounded-pill"
                    onClick={() => onDelete && onDelete(item)}
                    title={t('common.buttons.delete') || 'Delete'}
                  >
                    <i className="ri-delete-bin-line fs-5" />
                  </button>
                )}
              </div>
            </td>
          );
        }

        return <td key={headCell.id}>{item[headCell.id]}</td>;
      })}
    </tr>
  );
};

export default DocumentRow;
